import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { IFileResponse, NotificationService } from '@lib/core';
import { InvoiceDataService } from '../../invoice-data.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, catchError, finalize, pipe, switchMap, tap } from 'rxjs';
import { InvoiceDetail } from '../types/InvoiceDetail';
import { BC, BreadcrumbService } from '@lib/shared/breadcrumbs';
import { marker } from '@colsen1991/ngx-translate-extract-marker';

interface IInvoiceDetailDisplayState {
    readonly invoice?: InvoiceDetail;
    readonly loadError?: number;
    readonly loading: boolean;
}

const initialState: IInvoiceDetailDisplayState = {
    invoice: undefined,
    loadError: undefined,
    loading: false,
};

export const InvoiceDetailDisplayStore = signalStore(
    withState(initialState),
    withMethods(
        (
            store,
            dataService = inject(InvoiceDataService),
            breadcrumbService = inject(BreadcrumbService),
            notificationService = inject(NotificationService),
        ) => ({
            filesChanged(updatedFilesList: IFileResponse[]): void {
                patchState(store, {
                    invoice: store.invoice().updateFiles(updatedFilesList),
                });
            },
            loadData: rxMethod<string>(
                pipe(
                    tap((id: string) => {
                        if (store.invoice()?.id !== id) {
                            patchState(store, { invoice: undefined });
                        }
                        patchState(store, {
                            loadError: undefined,
                            loading: true,
                        });
                        breadcrumbService.set(
                            BC.MY_ADMIN.LIST,
                            BC.MY_ADMIN.INVOICE.LIST,
                            BC.MY_ADMIN.INVOICE.DETAIL(id, marker('Rechnung')),
                        );
                    }),
                    tap(() => notificationService.spinner.show()),
                    switchMap((id: string) =>
                        dataService.getStandaloneInvoice(id).pipe(
                            tap({
                                next: (invoice: InvoiceDetail) => {
                                    patchState(store, {
                                        invoice,
                                        loadError: undefined,
                                    });
                                },
                            }),
                            tap((invoice) =>
                                breadcrumbService.set(
                                    BC.MY_ADMIN.LIST,
                                    BC.MY_ADMIN.INVOICE.LIST,
                                    BC.MY_ADMIN.INVOICE.DETAIL(invoice.id, invoice.invoiceNumber),
                                ),
                            ),
                            catchError((error: HttpErrorResponse) => {
                                patchState(store, { invoice: undefined, loadError: error.status });
                                return EMPTY;
                            }),
                            finalize(() => {
                                notificationService.spinner.hide();
                                patchState(store, { loading: false });
                            }),
                        ),
                    ),
                ),
            ),

            markInvoicePaid: rxMethod<void>(
                pipe(
                    switchMap(() =>
                        dataService.markInvoicesPaid(store.invoice().id).pipe(
                            switchMap(() => {
                                // Refresh invoice data after successful API call
                                return dataService.getStandaloneInvoice(store.invoice().id).pipe(
                                    tap({
                                        next: (invoice: InvoiceDetail) => {
                                            patchState(store, {
                                                invoice,
                                            });
                                        },
                                    }),
                                    tap((invoice) =>
                                        breadcrumbService.set(
                                            BC.MY_ADMIN.LIST,
                                            BC.MY_ADMIN.INVOICE.LIST,
                                            BC.MY_ADMIN.INVOICE.DETAIL(invoice.id, invoice.invoiceNumber),
                                        ),
                                    ),
                                );
                            }),
                        ),
                    ),
                ),
            ),
        }),
    ),
);
